<template>
  <main dir="rtl" class="flex-1 pb-8 px-4 sm:px-6 lg:px-8">
    <div class="mx-auto">
      <div class="mb-6">
        <h1 class="text-2xl font-bold text-primary">حجز وإلغاء مواعيد الطلاب</h1>
        <p class="text-sm text-gray-600 mt-1">اختيار معلم ثم موعد متاح ثم الطالب لإتمام الحجز</p>
      </div>

      <div class="mb-4 flex gap-2">
        <button
          @click="activeTab = 'book'"
          :class="activeTab === 'book' ? 'bg-primary text-white' : 'bg-white text-gray-700 border'"
          class="px-4 py-2 rounded-md transition-colors"
        >
          حجز موعد
        </button>
        <button
          @click="activeTab = 'cancel'"
          :class="activeTab === 'cancel' ? 'bg-primary text-white' : 'bg-white text-gray-700 border'"
          class="px-4 py-2 rounded-md transition-colors"
        >
          إلغاء موعد
        </button>
      </div>

      <div v-if="errorMessage" class="mb-4 rounded bg-red-100 border border-red-300 text-red-700 px-4 py-3">
        {{ errorMessage }}
      </div>
      <div v-if="successMessage" class="mb-4 rounded bg-green-100 border border-green-300 text-green-700 px-4 py-3">
        {{ successMessage }}
      </div>

      <section v-if="activeTab === 'book'" class="bg-white rounded-lg shadow p-5">
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label class="block text-sm text-gray-700 mb-2">المعلم</label>
            <select
              v-model="selectedTeacherId"
              @change="onTeacherChange"
              class="w-full border border-gray-300 rounded-md px-3 py-2"
            >
              <option value="">اختر المعلم</option>
              <option v-for="teacher in teachers" :key="teacher.teacherId" :value="teacher.teacherId">
                {{ teacher.teacherName }}
              </option>
            </select>
          </div>
          <div>
            <label class="block text-sm text-gray-700 mb-2">الطالب</label>
            <select v-model="selectedStudentId" class="w-full border border-gray-300 rounded-md px-3 py-2">
              <option value="">اختر الطالب</option>
              <option v-for="student in students" :key="student.studentId" :value="student.studentId">
                {{ student.studentName }}
              </option>
            </select>
          </div>
        </div>

        <div class="mt-5">
          <div class="flex justify-between items-center mb-3">
            <h3 class="font-semibold text-gray-800">المواعيد المتاحة</h3>
            <button @click="loadSlots" :disabled="!selectedTeacherId || slotsLoading" class="text-sm px-3 py-1 border rounded">
              {{ slotsLoading ? 'جاري التحميل...' : 'تحديث' }}
            </button>
          </div>
          <div v-if="!selectedTeacherId" class="text-sm text-gray-500">اختر المعلم أولًا لعرض المواعيد.</div>
          <div v-else-if="slots.length === 0 && !slotsLoading" class="text-sm text-gray-500">لا توجد مواعيد متاحة.</div>
          <div v-else class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            <label
              v-for="slot in slots"
              :key="slot.slotKey"
              class="border rounded-md p-3 cursor-pointer"
              :class="selectedSlotKey === slot.slotKey ? 'border-primary bg-amber-50' : 'border-gray-200'"
            >
              <input type="radio" class="ml-2" :value="slot.slotKey" v-model="selectedSlotKey" />
              <span class="font-medium">{{ slot.dayName || 'كل الأيام' }}</span>
              <div class="text-sm text-gray-700 mt-1">{{ slot.startTimeAr || slot.startTimeEn }} - {{ slot.endTimeAr || slot.endTimeEn }}</div>
            </label>
          </div>
        </div>

        <div class="mt-5">
          <button
            @click="openBookConfirmation"
            :disabled="bookingLoading || !selectedTeacherId || !selectedStudentId || !selectedSlot"
            class="px-4 py-2 bg-primary text-white rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {{ bookingLoading ? 'جاري الحجز...' : 'تأكيد الحجز' }}
          </button>
        </div>
      </section>

      <section v-else class="bg-white rounded-lg shadow p-5">
        <div class="flex flex-wrap items-end gap-3 mb-4">
          <div>
            <label class="block text-sm text-gray-700 mb-1">تصفية بالمعلم</label>
            <select v-model="cancelFilterTeacherId" class="border border-gray-300 rounded-md px-3 py-2 min-w-[220px]">
              <option value="">الكل</option>
              <option v-for="teacher in teachers" :key="'cancel-' + teacher.teacherId" :value="teacher.teacherId">
                {{ teacher.teacherName }}
              </option>
            </select>
          </div>
          <button @click="loadBookings" class="px-4 py-2 border rounded-md">{{ bookingsLoading ? 'جاري التحميل...' : 'بحث' }}</button>
        </div>

        <div class="overflow-x-auto">
          <table class="min-w-full divide-y divide-gray-200">
            <thead class="bg-gray-50">
              <tr>
                <th class="px-4 py-2 text-right text-xs text-gray-600">الطالب</th>
                <th class="px-4 py-2 text-right text-xs text-gray-600">المعلم</th>
                <th class="px-4 py-2 text-right text-xs text-gray-600">الوقت</th>
                <th class="px-4 py-2 text-right text-xs text-gray-600">الإجراءات</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-100">
              <tr v-for="booking in bookings" :key="booking.id">
                <td class="px-4 py-3 text-sm">{{ booking.studentName }}</td>
                <td class="px-4 py-3 text-sm">{{ booking.teacherName }}</td>
                <td class="px-4 py-3 text-sm">{{ formatTime(booking.startTime) }} - {{ formatTime(booking.endTime) }}</td>
                <td class="px-4 py-3 text-sm">
                  <button
                    @click="openCancelConfirmation(booking)"
                    :disabled="cancelLoadingId === booking.id"
                    class="px-3 py-1 bg-red-500 text-white rounded-md disabled:opacity-50"
                  >
                    {{ cancelLoadingId === booking.id ? 'جاري الإلغاء...' : 'إلغاء الموعد' }}
                  </button>
                </td>
              </tr>
              <tr v-if="bookings.length === 0 && !bookingsLoading">
                <td colspan="4" class="text-center text-sm text-gray-500 py-4">لا توجد حجوزات نشطة</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </div>

    <div
      v-if="confirmationModal.visible"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 px-4"
    >
      <div class="w-full max-w-md rounded-lg bg-white shadow-xl">
        <div class="border-b px-5 py-4">
          <h3 class="text-base font-semibold text-gray-900">{{ confirmationModal.title }}</h3>
        </div>
        <div class="px-5 py-4">
          <p class="text-sm text-gray-700 whitespace-pre-line">{{ confirmationModal.message }}</p>
        </div>
        <div class="flex justify-end gap-2 border-t px-5 py-3">
          <button
            type="button"
            class="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
            @click="closeConfirmation"
          >
            إلغاء
          </button>
          <button
            type="button"
            class="px-4 py-2 rounded-md text-white"
            :class="confirmationModal.confirmType === 'danger' ? 'bg-red-500 hover:bg-red-600' : 'bg-primary'"
            @click="onConfirmAction"
          >
            تأكيد
          </button>
        </div>
      </div>
    </div>
  </main>
</template>

<script>
import axios from 'axios';
import url from '@/url';

export default {
  name: 'AdminStudentBooking',
  data() {
    return {
      activeTab: 'book',
      teachers: [],
      students: [],
      slots: [],
      bookings: [],
      selectedTeacherId: '',
      selectedStudentId: '',
      selectedSlotKey: '',
      cancelFilterTeacherId: '',
      slotsLoading: false,
      bookingsLoading: false,
      bookingLoading: false,
      cancelLoadingId: null,
      errorMessage: '',
      successMessage: '',
      confirmationModal: {
        visible: false,
        title: '',
        message: '',
        action: null,
        confirmType: 'primary',
      },
    };
  },
  computed: {
    selectedSlot() {
      return this.slots.find((slot) => slot.slotKey === this.selectedSlotKey) || null;
    },
    authHeaders() {
      return { 'X-Authorization': localStorage.getItem('token') };
    },
  },
  async mounted() {
    await Promise.all([this.loadTeachers(), this.loadStudents(), this.loadBookings()]);
  },
  methods: {
    clearMessages() {
      this.errorMessage = '';
      this.successMessage = '';
    },
    formatApiError(err, fallback) {
      return (
        err?.response?.data?.result?.mesAr ||
        err?.response?.data?.messageAr ||
        err?.response?.data?.message ||
        fallback
      );
    },
    formatTime(time) {
      if (!time) return '-';
      const [h, m] = time.split(':');
      const d = new Date();
      d.setHours(Number(h), Number(m), 0, 0);
      return d.toLocaleTimeString('ar-SA', { hour: '2-digit', minute: '2-digit', hour12: true });
    },
    buildSlotKey(slot) {
      return `${slot.sessionTimeId}-${slot.startTimeSort || slot.startTimeEn}-${slot.endTimeSort || slot.endTimeEn}-${slot.dayName || 'all'}`;
    },
    async loadTeachers() {
      try {
        const res = await axios.get(process.env.VUE_APP_SERVICE_URL + url.admin_booking_teachers, { headers: this.authHeaders });
        this.teachers = res.data?.result?.data || res.data?.data || [];
      } catch (err) {
        this.errorMessage = this.formatApiError(err, 'تعذر تحميل قائمة المعلمين');
      }
    },
    async loadStudents() {
      try {
        const res = await axios.get(process.env.VUE_APP_SERVICE_URL + url.admin_booking_students, { headers: this.authHeaders });
        this.students = res.data?.result?.data || res.data?.data || [];
      } catch (err) {
        this.errorMessage = this.formatApiError(err, 'تعذر تحميل قائمة الطلاب');
      }
    },
    async onTeacherChange() {
      this.selectedSlotKey = '';
      this.slots = [];
      await this.loadSlots();
    },
    async loadSlots() {
      if (!this.selectedTeacherId) return;
      this.slotsLoading = true;
      this.clearMessages();
      try {
        const endpoint = `${process.env.VUE_APP_SERVICE_URL}${url.admin_booking_teacher_slots}${this.selectedTeacherId}/slots`;
        const res = await axios.get(endpoint, { headers: this.authHeaders });
        const rawSlots = res.data?.result?.data || res.data?.data || [];
        this.slots = rawSlots.map((slot) => ({ ...slot, slotKey: this.buildSlotKey(slot) }));
      } catch (err) {
        this.errorMessage = this.formatApiError(err, 'تعذر تحميل الأوقات المتاحة');
      } finally {
        this.slotsLoading = false;
      }
    },
    async bookSession() {
      if (!this.selectedTeacherId || !this.selectedStudentId || !this.selectedSlot) {
        this.errorMessage = 'يرجى اختيار المعلم والموعد والطالب';
        return;
      }
      this.bookingLoading = true;
      this.clearMessages();
      try {
        await axios.post(
          process.env.VUE_APP_SERVICE_URL + url.admin_booking_book,
          {
            studentId: Number(this.selectedStudentId),
            teacherId: Number(this.selectedTeacherId),
            sessionTimeId: Number(this.selectedSlot.sessionTimeId),
            slotStartTime: this.selectedSlot.startTimeSort || null,
            slotEndTime: this.selectedSlot.endTimeSort || null,
          },
          { headers: this.authHeaders }
        );
        this.successMessage = 'تم حجز الموعد بنجاح';
        this.selectedStudentId = '';
        this.selectedSlotKey = '';
        await Promise.all([this.loadStudents(), this.loadSlots(), this.loadBookings()]);
      } catch (err) {
        this.errorMessage = this.formatApiError(err, 'فشل في حجز الموعد');
      } finally {
        this.bookingLoading = false;
      }
    },
    openBookConfirmation() {
      const teacher = this.teachers.find((t) => t.teacherId === Number(this.selectedTeacherId));
      const student = this.students.find((s) => s.studentId === Number(this.selectedStudentId));
      const timeFrom = this.selectedSlot?.startTimeAr || this.selectedSlot?.startTimeEn || '-';
      const timeTo = this.selectedSlot?.endTimeAr || this.selectedSlot?.endTimeEn || '-';

      this.confirmationModal = {
        visible: true,
        title: 'تأكيد الحجز',
        message: `سيتم حجز الموعد التالي:\nالمعلم: ${teacher?.teacherName || '-'}\nالطالب: ${student?.studentName || '-'}\nالوقت: ${timeFrom} - ${timeTo}`,
        action: () => this.bookSession(),
        confirmType: 'primary',
      };
    },
    async loadBookings() {
      this.bookingsLoading = true;
      this.clearMessages();
      try {
        const params = {};
        if (this.cancelFilterTeacherId) params.teacherId = this.cancelFilterTeacherId;
        const res = await axios.get(process.env.VUE_APP_SERVICE_URL + url.admin_booking_bookings, {
          params,
          headers: this.authHeaders,
        });
        this.bookings = res.data?.result?.data || res.data?.data || [];
      } catch (err) {
        this.errorMessage = this.formatApiError(err, 'تعذر تحميل الحجوزات');
      } finally {
        this.bookingsLoading = false;
      }
    },
    openCancelConfirmation(booking) {
      this.confirmationModal = {
        visible: true,
        title: 'تأكيد إلغاء الموعد',
        message: `هل أنت متأكد من إلغاء الموعد؟\nالطالب: ${booking.studentName || '-'}\nالمعلم: ${booking.teacherName || '-'}\nالوقت: ${this.formatTime(booking.startTime)} - ${this.formatTime(booking.endTime)}`,
        action: () => this.cancelBooking(booking),
        confirmType: 'danger',
      };
    },
    closeConfirmation() {
      this.confirmationModal = {
        visible: false,
        title: '',
        message: '',
        action: null,
        confirmType: 'primary',
      };
    },
    onConfirmAction() {
      const action = this.confirmationModal.action;
      this.closeConfirmation();
      if (typeof action === 'function') {
        action();
      }
    },
    async cancelBooking(booking) {
      this.cancelLoadingId = booking.id;
      this.clearMessages();
      try {
        await axios.delete(`${process.env.VUE_APP_SERVICE_URL}${url.admin_booking_bookings}/${booking.id}`, {
          headers: this.authHeaders,
        });
        this.successMessage = 'تم إلغاء الموعد بنجاح';
        await Promise.all([this.loadStudents(), this.loadBookings(), this.loadSlots()]);
      } catch (err) {
        this.errorMessage = this.formatApiError(err, 'فشل إلغاء الموعد');
      } finally {
        this.cancelLoadingId = null;
      }
    },
  },
};
</script>

<style scoped>
.text-primary {
  color: #c9a06c;
}

.bg-primary {
  background-color: #c9a06c;
}
</style>
